import * as React from 'react';
import { DayOfWeek, LocalDate, TemporalAdjusters } from 'js-joda';
import _ from 'lodash';
import clsx from 'clsx';

import { DisplayMonth, DisplayWeekday } from '@tock/time';
import { IconChevronSmallLeft, IconChevronSmallRight } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { Typography } from 'omakase';
import { ifClickable } from '@tock/shared/utils/touchUtils';
import { range } from 'utils/calendar';
import BaseComponent from '@tock/shared/components/BaseComponent';
import ConsumerCalendarDay from 'components/calendar/ConsumerCalendarDay';

const weekdaysArray = [0, 1, 2, 3, 4, 5, 6];

interface Props {
  availableDates?: DateAvailabilityObject;
  availabilityOverride?: boolean;
  disableNext?: boolean;
  disablePrev?: boolean;
  endDate?: LocalDate;
  focusDate?: LocalDate;
  handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, day: string): void;
  lockedDates?: DateAvailabilityObject;
  month: LocalDate;
  offeredDates?: DateAvailabilityObject;
  openDates: DateAvailabilityObject;
  selectedDates: string[];
  soldDates?: DateAvailabilityObject;
  onClickNext(): void;
  onClickPrev(): void;
  onSelectDate(date: string): void;
  ignoreYear?: boolean;
  'data-testid'?: string;
}

interface DateAvailabilityObject {
  [date: string]: boolean;
}

interface Day {
  key: string;
  text: string;
  date: string;
  focused: boolean;
  handleKeyDown(event: React.SyntheticEvent, day: string): void;
  isOutOfMonth: boolean;
  isPast: boolean;
  isFuture: boolean;
  isToday: boolean;
  isAvailable?: boolean;
  isSelected: boolean;
  isEnabled: boolean;
  isSoldOut: boolean;
  isClosed: boolean;
  isLocked: boolean;
  isNotAvailable: boolean;
  onClick(date: string): void;
}

class ConsumerCalendarMonth extends BaseComponent<Props> {
  handleSelectDate(date: string) {
    this.props.onSelectDate(date);
  }

  handleClickNext(e: React.SyntheticEvent) {
    e.preventDefault();
    this.props.onClickNext();
  }

  handleClickPrev(e: React.SyntheticEvent) {
    e.preventDefault();
    this.props.onClickPrev();
  }

  render() {
    const {
      availabilityOverride,
      availableDates,
      handleKeyDown,
      ignoreYear,
      lockedDates,
      month,
      offeredDates,
      openDates,
      selectedDates,
      soldDates,
    } = this.props;
    const today = LocalDate.now();
    const { max } = range(openDates);
    const definedDate = this.props.endDate ? this.props.endDate : LocalDate.now();
    const maxDate = max ? LocalDate.parse(max) : definedDate;
    const days: Day[] = [];
    const startOfMonth = month.with(TemporalAdjusters.firstDayOfMonth());
    let cursor = startOfMonth;
    if (cursor.dayOfWeek() !== DayOfWeek.SUNDAY) {
      cursor = cursor.minusDays(cursor.dayOfWeek().value());
    }
    let endOfMonth = month.with(TemporalAdjusters.lastDayOfMonth());
    // If the month ends on a Sunday, then the end of that isoWeek - 1 is the saturday before the end of the month.
    // In that edge case, we need to render an extra week to get to the next Saturday, so add seven days
    if (endOfMonth.dayOfWeek() === DayOfWeek.SUNDAY) {
      endOfMonth = endOfMonth.plusDays(7);
    }
    const endDate = endOfMonth.plusDays(6 - endOfMonth.dayOfWeek().value());
    let focusDate = this.props.focusDate;
    while (!cursor.isAfter(endDate)) {
      const isPast = cursor.isBefore(today);
      const isFuture = cursor.isAfter(maxDate);
      const isToday = cursor.isEqual(today);
      const curDate = cursor.toString();
      const isClosed = _.isEmpty(openDates)
        ? isPast || isFuture
        : !isPast && !isFuture && !openDates[curDate];
      const isAvailable = availabilityOverride
        ? !isPast && !isClosed
        : availableDates && !!availableDates[curDate];
      const isLocked = lockedDates ? !!lockedDates[curDate] && !isAvailable : false;
      const isSoldOut = soldDates ? !!soldDates[curDate] && !isAvailable && !isLocked : false;
      const isOffered = offeredDates ? !!offeredDates[curDate] : false;
      const isSelected = _.includes(selectedDates, curDate);
      const isEnabled =
        !isFuture && (isAvailable || isLocked || isSoldOut || isOffered) && !isClosed;
      const isNotAvailable =
        !isPast && !isFuture && !isAvailable && !isSoldOut && !isLocked && !isClosed;
      let focused = false;
      if (focusDate && focusDate.isEqual(cursor)) {
        if (isEnabled) {
          focused = true;
        } else {
          focusDate = focusDate.plusDays(1);
        }
      }

      const day: Day = {
        key: curDate,
        text: `${cursor.dayOfMonth()}`,
        date: curDate,
        focused,
        handleKeyDown,
        isOutOfMonth: cursor.month() !== month.month(),
        isPast,
        isFuture,
        isToday,
        isAvailable,
        isSelected,
        isEnabled,
        isSoldOut,
        isClosed,
        isLocked,
        isNotAvailable,
        onClick: this.handleSelectDate,
      };

      days.push(day);
      cursor = cursor.plusDays(1);
    }

    return (
      <div className="ConsumerCalendar-month" data-testid={this.props['data-testid']}>
        <div className="ConsumerCalendar-header">
          <button
            aria-label="Go to previous month"
            className={clsx('ConsumerCalendar-headerNav', 'ConsumerCalendar-headerNavPrev', {
              'is-disabled': this.props.disablePrev,
            })}
            onClick={
              !this.props.disablePrev
                ? ifClickable(this.handleClickPrev, 'calendar prev')
                : undefined
            }
            disabled={this.props.disablePrev}
            tabIndex={this.props.disablePrev ? undefined : 0}
            data-testid={'calendar-prev-button_' + this.props['data-testid']}
          >
            <IconMigrationUtil
              legacyIcon={<i className="tock-icon icon_small-arrow-left" />}
              newIcon={<IconChevronSmallLeft />}
            />
          </button>

          <div
            className="ConsumerCalendar-monthHeading"
            id={`consumer-calendar-month-${this.props['data-testid']}`}
            data-testid={'month-heading_' + this.props['data-testid']}
          >
            <Typography color="inherit" component="span" variant="body02Strong">
              <DisplayMonth displayMonthAs="long" monthValue={month} showYear={!ignoreYear} />
            </Typography>
          </div>

          <button
            aria-label="Go to next month"
            className={clsx('ConsumerCalendar-headerNav', 'ConsumerCalendar-headerNavNext', {
              'is-disabled': this.props.disableNext,
            })}
            onClick={
              !this.props.disableNext
                ? ifClickable(this.handleClickNext, 'calendar next')
                : undefined
            }
            disabled={this.props.disableNext}
            tabIndex={this.props.disableNext ? undefined : 0}
            data-testid={'calendar-next-button_' + this.props['data-testid']}
          >
            <IconMigrationUtil
              legacyIcon={<i className="tock-icon icon_small-arrow-right" />}
              newIcon={<IconChevronSmallRight />}
            />
          </button>
        </div>

        <div className="ConsumerCalendar-weekdays">
          {_.map(weekdaysArray, (weekday) => (
            <div className="ConsumerCalendar-weekday" key={weekday}>
              <Typography color="inherit" component="span" variant="body02">
                <DisplayWeekday displayWeekdayAs="short" weekdayValue={weekday} />
              </Typography>
            </div>
          ))}
        </div>

        {_.map(_.chunk(days, 7), (weekdays, idx) => (
          <div key={idx} className="ConsumerCalendar-week">
            {_.map(weekdays, (day) => (
              <ConsumerCalendarDay {...day} />
            ))}
          </div>
        ))}
      </div>
    );
  }
}

export default ConsumerCalendarMonth;
